import { useState, useEffect } from 'react';
import StickyPageHeader from '../common/StickyPageHeader';
import useModalBack from '../../hooks/useModalBack';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import {
  Plus,
  Edit2,
  Trash2,
  Building2,
  AlertTriangle,
  Eye,
  EyeOff,
  ArrowLeftRight,
  TrendingUp,
  TrendingDown
} from 'lucide-react';
import { useToast } from '../../context/ToastContext';
import { useConfig } from '../../context/ConfigContext';
import { useQueryClient } from '@tanstack/react-query';
import { useAccountsWithBalancesQuery, useDeleteAccount } from '../../hooks/useQueries';
import { accountAPI } from '../../services/api';
import { getIconComponent } from '../common/IconPicker';
import { AccountPageSkeleton } from '../common/Skeleton';

const ACCOUNT_TYPE_LABELS = {
  bank: 'Bank Account',
  wallet: 'Digital Wallet',
  cash: 'Cash',
  credit_card: 'Cards',
  other: 'Other'
};

export default function AccountPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { formatAmount, config } = useConfig();

  const queryClient = useQueryClient();
  const { data: accounts = [], isLoading } = useAccountsWithBalancesQuery();
  const deleteAccountMutation = useDeleteAccount();

  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, account: null });
  const [permanentDelete, setPermanentDelete] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showHidden, setShowHidden] = useState(false);

  // Server's canonical hidden flag is `isHidden`. Older records used
  // `isActive: false` for the same thing, so accept either.
  const hiddenCount = accounts.filter(
    a => a.isHidden === true || a.isActive === false,
  ).length;
  useEffect(() => {
    if (showHidden && hiddenCount === 0) {
      setShowHidden(false);
    }
  }, [showHidden, hiddenCount]);

  useModalBack(deleteConfirm.open, () => setDeleteConfirm({ open: false, account: null }), 'AccountDelete');

  const handleAddNew = () => navigate('/accounts/new');
  const handleEdit = (account) => navigate(`/accounts/${account._id}/edit`);
  const handleViewTransactions = (account) => {
    navigate(`/transactions?account=${encodeURIComponent(account.name)}&period=thisMonth`);
  };

  const handleDeleteClick = (account) => {
    setPermanentDelete(false);
    setDeleteConfirm({ open: true, account });
  };

  const handleDeleteClose = () => {
    setDeleteConfirm({ open: false, account: null });
    setPermanentDelete(false);
  };

  const handleToggleVisibility = async (account) => {
    const wasHidden = account.isHidden === true || account.isActive === false;
    const newIsHidden = !wasHidden;
    const queryKey = ['accountsWithBalances'];

    queryClient.setQueryData(queryKey, (old) =>
      old?.map(a => a._id === account._id ? { ...a, isHidden: newIsHidden, isActive: !newIsHidden } : a)
    );
    toast.success(newIsHidden ? `"${account.name}" hidden` : `"${account.name}" visible`);

    try {
      await accountAPI.update(account._id, { isHidden: newIsHidden });
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      queryClient.invalidateQueries({ queryKey: ['accountsWithBalances'] });
    } catch {
      queryClient.setQueryData(queryKey, (old) =>
        old?.map(a => a._id === account._id ? { ...a, isHidden: !newIsHidden, isActive: newIsHidden } : a)
      );
      toast.error('Failed to update account');
    }
  };

  const handleDeleteConfirm = async () => {
    if (deleteConfirm.account) {
      setDeleteLoading(true);
      try {
        const result = await deleteAccountMutation.mutateAsync({
          id: deleteConfirm.account._id,
          permanent: permanentDelete
        });
        if (permanentDelete) {
          toast.success(`"${deleteConfirm.account.name}" and ${result.deletedTransactions || 0} transaction(s) permanently deleted`);
        } else {
          toast.success(`"${deleteConfirm.account.name}" deleted. Create an account with the same name to restore.`);
        }
        handleDeleteClose();
      } catch {
        toast.error('Failed to delete account');
      } finally {
        setDeleteLoading(false);
      }
    }
  };

  if (isLoading && accounts.length === 0) {
    return <AccountPageSkeleton />;
  }

  // `isHidden` is the server's canonical visibility flag. Older records
  // used `isActive: false` for the same thing, so accept either until
  // every user has been migrated.
  const isHiddenAccount = (a) => a.isHidden === true || a.isActive === false;
  const activeAccounts = accounts.filter(a => !isHiddenAccount(a));
  const hiddenAccounts = accounts.filter(isHiddenAccount);
  const displayedAccounts = showHidden ? hiddenAccounts : activeAccounts;

  // Totals are tab-scoped: the visible tab shows visible-account sums,
  // the hidden tab shows hidden-account sums. Summing `accounts` as a
  // whole would leak hidden balances into the visible total and
  // disagree with the dashboard (which already excludes hidden).
  const sumBalance = (list) =>
    list.reduce((sum, a) => sum + (a.currentBalance ?? a.balance ?? 0), 0);
  const sumIncome = (list) =>
    list.reduce((sum, a) => sum + (a.totalIncome || 0), 0);
  const sumExpense = (list) =>
    list.reduce((sum, a) => sum + (a.totalExpense || 0), 0);

  const visibleBalance = sumBalance(activeAccounts);
  const visibleIncome = sumIncome(activeAccounts);
  const visibleExpense = sumExpense(activeAccounts);
  const hiddenBalance = sumBalance(hiddenAccounts);
  const hiddenIncome = sumIncome(hiddenAccounts);
  const hiddenExpense = sumExpense(hiddenAccounts);

  // Numbers shown in the top-row cards + mobile hero — switch with tab.
  const totalBalance = showHidden ? hiddenBalance : visibleBalance;
  const totalIncome = showHidden ? hiddenIncome : visibleIncome;
  const totalExpense = showHidden ? hiddenExpense : visibleExpense;

  return (
    <div className="space-y-3 md:space-y-4">
      {/* ========== MOBILE ========== */}

      {/* Mobile Header */}
      <StickyPageHeader className="xl:hidden">
        <h1 className="text-xl font-extrabold text-surface-900">Accounts</h1>
      </StickyPageHeader>

      {/* Mobile Hero Balance Card */}
      <div className="xl:hidden rounded-2xl p-5 text-white relative overflow-hidden" style={{
        background: showHidden
          ? 'linear-gradient(135deg, #64748b, #475569)'
          : 'linear-gradient(135deg, var(--accent-bg), color-mix(in srgb, var(--accent-bg) 80%, black))'
      }}>
        <div className="absolute -top-5 -right-5 w-24 h-24 rounded-full bg-white/10" />
        <div className="absolute -bottom-8 -left-3 w-20 h-20 rounded-full bg-white/[0.06]" />
        <div className="relative">
          <p className="text-xs opacity-80 mb-1">{showHidden ? 'Hidden Balance' : 'Total Balance'}</p>
          <p className="text-[26px] font-extrabold mb-3" style={{ color: 'var(--accent-text, white)' }}>
            {formatAmount(totalBalance)}
          </p>
          <div className="flex gap-5">
            <div>
              <p className="text-[10px] opacity-70">Income</p>
              <p className="text-sm font-bold">{formatAmount(totalIncome)}</p>
            </div>
            <div>
              <p className="text-[10px] opacity-70">Expense</p>
              <p className="text-sm font-bold">{formatAmount(totalExpense)}</p>
            </div>
            <div>
              <p className="text-[10px] opacity-70">Accounts</p>
              <p className="text-sm font-bold">{displayedAccounts.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Segmented Control - only if hidden accounts exist */}
      {hiddenCount > 0 && (
        <div className="xl:hidden bg-surface-100 rounded-xl p-1 flex">
          <button
            onClick={() => setShowHidden(false)}
            className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all ${
              !showHidden ? 'bg-white shadow-sm' : 'text-surface-500'
            }`}
            style={!showHidden ? { color: 'var(--accent-bg)' } : undefined}
          >
            Active
          </button>
          <button
            onClick={() => setShowHidden(true)}
            className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all ${
              showHidden ? 'bg-white shadow-sm' : 'text-surface-500'
            }`}
            style={showHidden ? { color: 'var(--accent-bg)' } : undefined}
          >
            Hidden ({hiddenCount})
          </button>
        </div>
      )}

      {/* ========== DESKTOP ========== */}

      {/* Desktop Header */}
      <div className="hidden xl:flex xl:items-center xl:justify-between gap-4">
        <div className="page-header mb-0">
          <h1 className="page-title">Accounts</h1>
          <p className="page-subtitle">Manage your bank accounts and payment methods</p>
        </div>
        <div className="flex items-center gap-3">
          {hiddenCount > 0 && (
            <div className="bg-surface-100 rounded-xl p-1 flex">
              <button
                onClick={() => setShowHidden(false)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  !showHidden ? 'bg-white shadow-sm text-surface-900' : 'text-surface-500 hover:text-surface-700'
                }`}
              >
                Active ({activeAccounts.length})
              </button>
              <button
                onClick={() => setShowHidden(true)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  showHidden ? 'bg-white shadow-sm text-surface-900' : 'text-surface-500 hover:text-surface-700'
                }`}
              >
                Hidden ({hiddenCount})
              </button>
            </div>
          )}
          <button
            onClick={handleAddNew}
            className="inline-flex items-center justify-center px-5 py-3 rounded-xl font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all"
            style={{ backgroundColor: 'var(--accent-bg)', color: 'var(--accent-text)' }}
          >
            <Plus className="w-5 h-5 mr-2" />
            Add Account
          </button>
        </div>
      </div>

      {/* Desktop Summary Cards */}
      <div className="hidden xl:grid xl:grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl border border-surface-200 p-5">
          <p className="text-sm text-surface-500 mb-1">{showHidden ? 'Hidden Balance' : 'Total Balance'}</p>
          <p className="text-2xl font-extrabold text-surface-900">{formatAmount(totalBalance)}</p>
          {/* When on the Active tab, show the hidden balance as a
              subtle footnote so the user can see both numbers without
              switching tabs. */}
          {!showHidden && hiddenCount > 0 && (
            <p className="text-xs text-surface-400 mt-1">
              + {formatAmount(hiddenBalance)} hidden
            </p>
          )}
        </div>
        <div className="bg-white rounded-2xl border border-surface-200 p-5">
          <p className="text-sm text-surface-500 mb-1">Income</p>
          <p className="text-2xl font-extrabold" style={{ color: config.incomeColor }}>{formatAmount(totalIncome)}</p>
        </div>
        <div className="bg-white rounded-2xl border border-surface-200 p-5">
          <p className="text-sm text-surface-500 mb-1">Expense</p>
          <p className="text-2xl font-extrabold" style={{ color: config.expenseColor }}>{formatAmount(totalExpense)}</p>
        </div>
        <div className="bg-white rounded-2xl border border-surface-200 p-5">
          <p className="text-sm text-surface-500 mb-1">Accounts</p>
          <p className="text-2xl font-extrabold text-surface-900">
            {activeAccounts.length}
            {hiddenCount > 0 && (
              <span className="text-base font-normal text-surface-400 ml-1">
                ({hiddenCount} hidden)
              </span>
            )}
          </p>
        </div>
      </div>

      {/* ========== CONTENT ========== */}

      {!isLoading && accounts.length === 0 ? (
        <div className="card text-center py-12">
          <div className="w-16 h-16 bg-surface-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Building2 className="w-8 h-8 text-surface-400" />
          </div>
          <h3 className="text-lg font-medium text-surface-900 mb-2">No accounts yet</h3>
          <p className="text-surface-500 mb-4">Add your bank accounts and payment methods to track transactions</p>
          <button onClick={handleAddNew} className="btn-primary inline-flex">
            <Plus className="w-5 h-5 mr-2" />
            Add Account
          </button>
        </div>
      ) : displayedAccounts.length === 0 ? (
        <div className="card text-center py-10">
          <EyeOff className="w-10 h-10 text-surface-300 mx-auto mb-3" />
          <p className="text-sm text-surface-500">
            All accounts are hidden. Tap <button onClick={() => setShowHidden(true)} className="font-semibold text-surface-700 underline underline-offset-2">Hidden ({hiddenCount})</button> to view them.
          </p>
        </div>
      ) : (
        <>
          {/* Desktop Table View */}
          <div className="hidden xl:block bg-white rounded-2xl border border-surface-200 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-surface-200 bg-surface-50">
                  <th className="table-cell text-left font-semibold text-surface-600">Account</th>
                  <th className="table-cell w-28 text-left font-semibold text-surface-600">Type</th>
                  <th className="table-cell w-36 text-right font-semibold text-surface-600">Income</th>
                  <th className="table-cell w-36 text-right font-semibold text-surface-600">Expense</th>
                  <th className="table-cell w-40 text-right font-semibold text-surface-600">Balance</th>
                  <th className="table-cell text-right w-32 font-semibold text-surface-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-100">
                {displayedAccounts.map((account) => {
                  const Icon = getIconComponent(account.icon);
                  const color = account.color || '#6366f1';
                  const hidden = isHiddenAccount(account);
                  return (
                    <tr key={account._id} className={`hover:bg-surface-50 transition-colors ${hidden ? 'opacity-60' : ''}`}>
                      <td className="table-cell">
                        <div className="flex items-center gap-3">
                          <div
                            className="w-10 h-10 rounded-xl flex items-center justify-center"
                            style={{ backgroundColor: `${color}15` }}
                          >
                            <Icon className="w-5 h-5" style={{ color: hidden ? '#94a3b8' : color }} fill={hidden ? '#94a3b8' : color} fillOpacity={0.15} />
                          </div>
                          <span className="font-semibold text-surface-900">{account.name}</span>
                        </div>
                      </td>
                      <td className="table-cell">
                        <span className="text-sm text-surface-500">{ACCOUNT_TYPE_LABELS[account.type] || account.type}</span>
                      </td>
                      <td className="table-cell text-right">
                        <span className="text-sm font-medium" style={{ color: config.incomeColor }}>
                          {formatAmount(account.totalIncome || 0)}
                        </span>
                      </td>
                      <td className="table-cell text-right">
                        <span className="text-sm font-medium" style={{ color: config.expenseColor }}>
                          {formatAmount(account.totalExpense || 0)}
                        </span>
                      </td>
                      <td className="table-cell text-right">
                        <span className="text-sm font-bold text-surface-900">
                          {formatAmount(account.currentBalance ?? account.balance ?? 0)}
                        </span>
                      </td>
                      <td className="table-cell text-right">
                        <div className="flex items-center justify-end gap-0.5">
                          <button onClick={() => handleViewTransactions(account)} className="p-2 text-surface-400 hover:text-surface-700 hover:bg-surface-100 rounded-lg transition-colors" title="View transactions">
                            <ArrowLeftRight className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleToggleVisibility(account)} className="p-2 text-surface-400 hover:text-surface-600 hover:bg-surface-100 rounded-lg transition-colors" title={hidden ? 'Show account' : 'Hide account'}>
                            {hidden ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                          <button onClick={() => handleEdit(account)} className="p-2 text-primary-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors" title="Edit">
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleDeleteClick(account)} className="p-2 text-expense-500 hover:text-expense-600 hover:bg-expense-50 rounded-lg transition-colors" title="Delete">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="xl:hidden space-y-3 pb-20">
            {displayedAccounts.map((account) => {
              const Icon = getIconComponent(account.icon);
              const color = account.color || '#6366f1';
              const isHidden = isHiddenAccount(account);
              return (
                <div
                  key={account._id}
                  className={`bg-white rounded-2xl p-4 shadow-sm ${isHidden ? 'opacity-75 border border-dashed border-surface-300' : ''}`}
                >
                  {/* Top row: icon, name, balance */}
                  <div className="flex items-center gap-3 mb-3">
                    <div
                      className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${color}15` }}
                    >
                      <Icon className="w-[22px] h-[22px]" style={{ color: isHidden ? '#94a3b8' : color }} fill={isHidden ? '#94a3b8' : color} fillOpacity={0.15} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`font-bold text-[15px] truncate ${isHidden ? 'text-surface-500' : 'text-surface-900'}`}>
                        {account.name}
                      </p>
                      <p className="text-[11px] text-surface-400">{ACCOUNT_TYPE_LABELS[account.type]}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className={`font-extrabold text-[17px] ${isHidden ? 'text-surface-500' : 'text-surface-900'}`}>
                        {formatAmount(account.currentBalance ?? account.balance ?? 0)}
                      </p>
                    </div>
                  </div>

                  {/* Income/Expense chips */}
                  <div className="flex gap-2 mb-3">
                    <div className={`flex-1 rounded-lg px-3 py-2 flex items-center gap-2 ${isHidden ? 'bg-surface-50' : 'bg-income-50'}`}>
                      <TrendingUp className={`w-3.5 h-3.5 ${isHidden ? 'text-surface-400' : ''}`} style={!isHidden ? { color: config.incomeColor } : undefined} />
                      <div>
                        <p className={`text-[9px] ${isHidden ? 'text-surface-400' : ''}`} style={!isHidden ? { color: config.incomeColor } : undefined}>Income</p>
                        <p className={`text-xs font-bold ${isHidden ? 'text-surface-400' : ''}`} style={!isHidden ? { color: config.incomeColor } : undefined}>
                          {formatAmount(account.totalIncome || 0)}
                        </p>
                      </div>
                    </div>
                    <div className={`flex-1 rounded-lg px-3 py-2 flex items-center gap-2 ${isHidden ? 'bg-surface-50' : 'bg-expense-50'}`}>
                      <TrendingDown className={`w-3.5 h-3.5 ${isHidden ? 'text-surface-400' : ''}`} style={!isHidden ? { color: config.expenseColor } : undefined} />
                      <div>
                        <p className={`text-[9px] ${isHidden ? 'text-surface-400' : ''}`} style={!isHidden ? { color: config.expenseColor } : undefined}>Expense</p>
                        <p className={`text-xs font-bold ${isHidden ? 'text-surface-400' : ''}`} style={!isHidden ? { color: config.expenseColor } : undefined}>
                          {formatAmount(account.totalExpense || 0)}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Action row */}
                  <div className="flex gap-1.5 pt-3 border-t border-surface-100">
                    {!isHidden && (
                      <button
                        onClick={() => handleViewTransactions(account)}
                        className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-surface-50 text-[11px] font-medium text-surface-600 active:bg-surface-100 transition-colors"
                      >
                        <ArrowLeftRight className="w-3.5 h-3.5" />
                        Txns
                      </button>
                    )}
                    <button
                      onClick={() => handleToggleVisibility(account)}
                      className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-[11px] font-semibold active:opacity-75 transition-colors ${
                        isHidden
                          ? 'bg-green-50 text-green-600'
                          : 'bg-surface-50 text-surface-600'
                      }`}
                    >
                      {isHidden ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                      {isHidden ? 'Show' : 'Hide'}
                    </button>
                    <button
                      onClick={() => handleEdit(account)}
                      className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-primary-50 text-[11px] font-medium text-primary-600 active:bg-primary-100 transition-colors"
                    >
                      <Edit2 className="w-3.5 h-3.5" />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteClick(account)}
                      className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-expense-50 text-[11px] font-medium text-expense-600 active:bg-expense-100 transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* FAB for Mobile */}
      <button
        onClick={handleAddNew}
        className="fab"
        style={{ opacity: 0.85 }}
        aria-label="Add account"
      >
        <Plus className="w-6 h-6" />
      </button>

      {/* Delete Confirmation - Bottom Sheet on Mobile, Modal on Desktop */}
      {deleteConfirm.open && createPortal(
        <>
          {/* Mobile Bottom Sheet */}
          <div className="xl:hidden fixed inset-0 z-50">
            <div className="absolute inset-0 bg-black/50" onClick={handleDeleteClose} />
            <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-2xl animate-slide-up safe-area-bottom">
              {/* Drag handle */}
              <div className="flex justify-center pt-3 pb-4">
                <div className="w-9 h-1 rounded-full bg-surface-300" />
              </div>

              <div className="px-5 pb-6">
                {/* Icon + Title */}
                <div className="text-center mb-5">
                  <div className="w-12 h-12 rounded-full bg-expense-50 flex items-center justify-center mx-auto mb-3">
                    <Trash2 className="w-6 h-6 text-expense-500" />
                  </div>
                  <h3 className="text-lg font-bold text-surface-900">Delete "{deleteConfirm.account?.name}"?</h3>
                  <p className="text-sm text-surface-500 mt-1">This will also delete all transactions</p>
                </div>

                {/* Permanent toggle */}
                <label className="flex items-center gap-3 p-3 bg-surface-50 rounded-xl cursor-pointer mb-5">
                  <input
                    type="checkbox"
                    checked={permanentDelete}
                    onChange={(e) => setPermanentDelete(e.target.checked)}
                    className="w-[18px] h-[18px] text-expense-600 border-surface-300 rounded focus:ring-expense-500"
                  />
                  <div>
                    <p className="text-sm font-semibold text-surface-900">Delete permanently</p>
                    <p className="text-xs text-surface-500 mt-0.5">
                      {permanentDelete ? 'Cannot be undone. All data lost forever.' : 'Uncheck to allow restore later'}
                    </p>
                  </div>
                </label>

                {/* Buttons */}
                <div className="flex gap-3">
                  <button
                    onClick={handleDeleteClose}
                    className="flex-1 py-3.5 rounded-xl font-semibold text-sm text-surface-700 bg-surface-100 active:bg-surface-200 transition-colors"
                    disabled={deleteLoading}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteConfirm}
                    className={`flex-1 py-3.5 rounded-xl font-semibold text-sm text-white transition-colors ${
                      permanentDelete ? 'bg-expense-600 active:bg-expense-700' : 'bg-amber-600 active:bg-amber-700'
                    }`}
                    disabled={deleteLoading}
                  >
                    {deleteLoading ? 'Deleting...' : permanentDelete ? 'Delete Forever' : 'Delete'}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Desktop Modal */}
          <div className="hidden xl:flex fixed inset-0 z-50 items-center justify-center p-4 bg-black/50" onClick={handleDeleteClose}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()}>
              <div className="text-center mb-5">
                <div className="w-12 h-12 rounded-full bg-expense-50 flex items-center justify-center mx-auto mb-3">
                  <Trash2 className="w-6 h-6 text-expense-500" />
                </div>
                <h3 className="text-lg font-bold text-surface-900">Delete "{deleteConfirm.account?.name}"?</h3>
                <p className="text-sm text-surface-500 mt-1">This will also delete all transactions</p>
              </div>

              <label className="flex items-center gap-3 p-3 bg-surface-50 rounded-xl cursor-pointer mb-5">
                <input
                  type="checkbox"
                  checked={permanentDelete}
                  onChange={(e) => setPermanentDelete(e.target.checked)}
                  className="w-[18px] h-[18px] text-expense-600 border-surface-300 rounded focus:ring-expense-500"
                />
                <div>
                  <p className="text-sm font-semibold text-surface-900">Delete permanently</p>
                  <p className="text-xs text-surface-500 mt-0.5">
                    {permanentDelete ? 'Cannot be undone. All data lost forever.' : 'Uncheck to allow restore later'}
                  </p>
                </div>
              </label>

              <div className="flex gap-3">
                <button
                  onClick={handleDeleteClose}
                  className="flex-1 py-3 rounded-xl font-semibold text-sm text-surface-700 bg-surface-100 hover:bg-surface-200 transition-colors"
                  disabled={deleteLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  className={`flex-1 py-3 rounded-xl font-semibold text-sm text-white transition-colors ${
                    permanentDelete ? 'bg-expense-600 hover:bg-expense-700' : 'bg-amber-600 hover:bg-amber-700'
                  }`}
                  disabled={deleteLoading}
                >
                  {deleteLoading ? 'Deleting...' : permanentDelete ? 'Delete Forever' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
}
